import { useState, useRef, useEffect } from "react";
import {
  Table, Button, Tag, Modal, Radio, Space, Dropdown, Typography, notification, Input, Form, InputNumber,
} from "antd";
import {
  CheckOutlined, CloseOutlined, DownOutlined, HistoryOutlined, EditOutlined,
} from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import { UPDATE_INVOICE, INCREMENT_INVOICE_REJECTION, INSERT_INVOICE_HISTORY, INSERT_VENDOR_NOTIFICATION, INSERT_PURCHASE_ORDER, UPDATE_PURCHASE_ORDER_STATUS } from "Util/mutations";
import { PaymentStatus, appConstant } from "Util/constants";
import leadingZeroes from "leading-zeroes";
import User from "Hooks/user";
import axios from "axios";

const sendEmail = ({ to, subject, htmlbody }) =>
  axios.post(`${process.env.REACT_APP_API}/zoho/mail/send`, {
    from: { address: "noreply@catch-value.com", name: "CatchValue" },
    to,
    subject,
    htmlbody,
  });

// --- Zoho Books payment integration (PM-25) -------------------------------
// On Final Approve we record the vendor payment in Zoho Books: open the bill
// (idempotent) then record a payment against it. This is record-only — it
// marks the bill paid in Zoho's books; it does NOT disburse money to the bank.
const ZOHO_PAYMENT_MODE = "banktransfer";


const todayYMD = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

const withAdminHeader = (userId) => ({ headers: { "x-admin-user-id": String(userId) } });

// Draft → open. No-op on the api side if the bill is already open/paid.
const openZohoBill = (invoiceId, userId) =>
  axios.post(`${process.env.REACT_APP_API}/zoho/books/bills/${invoiceId}/open`, {}, withAdminHeader(userId));

// Voids the open Zoho bill. Fired only on a terminal reject (no re-submit left)
// so an unpayable payable doesn't linger in Zoho Books. Idempotent on the api
// side if the bill is already void.
const voidZohoBill = (invoiceId, userId) =>
  axios.post(`${process.env.REACT_APP_API}/zoho/books/bills/${invoiceId}/void`, {}, withAdminHeader(userId));

// PM-44: amendment helpers — cancel old PO in Zoho, sync new PO, sync new Bill.
const cancelZohoPO = (purchaseOrderId, userId) =>
  axios.post(`${process.env.REACT_APP_API}/zoho/books/purchase-orders/${purchaseOrderId}/cancel`, {}, withAdminHeader(userId));

const syncZohoPO = (purchaseOrderId, userId) =>
  axios.post(`${process.env.REACT_APP_API}/zoho/books/purchase-orders/sync`, { purchase_order_id: purchaseOrderId }, withAdminHeader(userId));

const syncZohoBill = (invoiceId, userId) =>
  axios.post(`${process.env.REACT_APP_API}/zoho/books/bills/sync`, { invoice_id: invoiceId }, withAdminHeader(userId));

// Records one Zoho vendor payment covering every bill in a single-vendor group.
const recordZohoVendorPayment = (vendorTxList, userId) => {
  const amount = vendorTxList.reduce((sum, tx) => sum + Number(tx.payment ?? 0), 0);
  const ids = vendorTxList.map(tx => tx.id).sort((a, b) => a - b);
  return axios.post(
    `${process.env.REACT_APP_API}/zoho/books/vendor-payments/`,
    {
      amount,
      payment_mode: ZOHO_PAYMENT_MODE,
      reference_number: `VP-${ids.join("-")}`, // deterministic → idempotent on retry
      payment_date: todayYMD(),
      bill_applications: vendorTxList.map(tx => ({
        invoice_id: tx.id,
        amount_applied: Number(tx.payment ?? 0),
      })),
    },
    withAdminHeader(userId)
  );
};

// Records Zoho payments for the selected transactions, grouped per vendor
// (Zoho records one vendor per payment). Returns which invoices succeeded and
// per-invoice failure reasons so the caller can gate the approval.
const recordZohoPayments = async (txList, userId) => {
  const okIds = new Set();
  const failures = [];

  const byVendor = new Map();
  txList.forEach(tx => {
    if (!byVendor.has(tx.vendor)) byVendor.set(tx.vendor, []);
    byVendor.get(tx.vendor).push(tx);
  });

  for (const group of byVendor.values()) {
    try {
      const opened = await Promise.all(group.map(tx => openZohoBill(tx.id, userId)));
      // If every bill is already paid, the payment was recorded on a prior
      // attempt (e.g. a retry after the status update failed) — treat as done
      // rather than double-paying, which Zoho would reject.
      const allPaid = opened.every(r => r.data?.zoho_bill_status === "paid");
      if (!allPaid) await recordZohoVendorPayment(group, userId);
      group.forEach(tx => okIds.add(tx.id));
    } catch (err) {
      // 409 = a payment with this reference already exists → already recorded.
      if (err.response?.status === 409) {
        group.forEach(tx => okIds.add(tx.id));
        continue;
      }
      const reason = err.response?.data?.error_description ?? err.message;
      group.forEach(tx => failures.push({ id: tx.id, invoice: tx.invoice, reason }));
    }
  }

  return { okIds, failures };
};
// --------------------------------------------------------------------------

const { Text } = Typography;

const FINANCE_REASONS = [
  "Invoice Details Incorrect",
  "Amount Does Not Match Approved Value",
  "Missing or Invalid Supporting Details",
  "Vendor Bank Details Issue",
  "Compliance / Approval Pending",
  "Others",
];

const QC_REASONS = [
  "Contamination",
  "Spoilage / Off-Odour",
  "Quality Not Acceptable",
  "Temperature Issue",
  "Processing Non-Conformance",
  "Traceability Issue",
];

const DB_STATUS_MAP = {
  authorised: "PENDING APPROVAL",
  approved: "APPROVED",
  rejected: "REJECTED",
  paid: "PAID",
  for_finance_revisit: "FOR FINANCE REVISIT",
};

const DB_CORTEX_MAP = {
  authorised: "AUTHORISED",
  approved: "APPROVED",
  rejected: "REJECTED",
  paid: "PAID",
  for_finance_revisit: "FOR FINANCE REVISIT",
};

const formatInvoice = (inv) => {
  const createdAt = new Date(inv.created_at);
  // Use the most recent (highest-id) non-cancelled PO for display; fall back to most recent PO overall.
  const allPOs = inv.purchase_orders ?? [];
  const activePOs = allPOs.filter(p => p.zoho_po_status !== "cancelled" && p.status !== "rejected");
  const sortedActivePOs = [...activePOs].sort((a, b) => b.id - a.id);
  const po = sortedActivePOs[0] ?? [...allPOs].sort((a, b) => b.id - a.id)[0];
  return {
    id: inv.id,
    invoice: leadingZeroes(inv.id, appConstant.leadingZero),
    vendor: inv.user_id,
    vendorName: inv.users?.name ?? null,
    vendorEmail: inv.users?.email ?? null,
    location: inv.users?.user_location?.label ?? inv.location_id,
    total: inv.total_value ?? 0,
    adj: inv.adjustment ?? 0,
    payment: inv.payment_value ?? 0,
    date: createdAt.toLocaleDateString("en-IN", { day: "numeric", month: "numeric", year: "numeric" }),
    auction: po?.bid?.id ?? "—",
    poNumber: po ? `PO-${po.id}` : "—",
    // Raw PO rows needed for the amendment flow (cancel + insert new PO).
    purchaseOrders: inv.purchase_orders ?? [],
    zoho_bill_id: inv.zoho_bill_id ?? null,
    status: DB_STATUS_MAP[inv.status] ?? inv.status,
    cortexStatus: DB_CORTEX_MAP[inv.status] ?? inv.status,
    lastUpdatedBy: inv.updated_by_admin?.full_name ?? inv.updated_by ?? "System",
    lastUpdatedAt: createdAt,
    rejectionReason: inv.remarks ?? null,
    rejectionCount: inv.rejection_count ?? 0,
    paymentRef: inv.payment_reference ?? null,
    history: inv.invoice_history?.length
      ? inv.invoice_history.map(h => ({
          action: h.action,
          user: h.admin_user?.full_name ?? "System",
          time: new Date(h.created_at),
          comment: h.comment ?? "",
        }))
      : [{ action: "Submitted for Approval", user: "System", time: createdAt, comment: "Transaction submitted for approval workflow." }],
  };
};

const fmt = (n) =>
  `₹ ${Math.abs(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STATUS_COLOR = {
  "PENDING APPROVAL": "orange",
  "APPROVED": "green",
  "REJECTED": "red",
  "PAID": "blue",
  "FOR FINANCE REVISIT": "gold",
};

// Reject / Finance Revisit Modal
// mode="reject"  → final rejection (red confirm button)
// mode="revisit" → for finance revisit (amber confirm button)
function RejectModal({ open, transactions, onConfirm, onClose, mode = "reject" }) {
  const { userRoles } = User();
  const isFinance = userRoles.includes(8);
  const isQC = userRoles.includes(9);
  const defaultCategory = isQC && !isFinance ? "qc" : "finance";
  const showCategorySwitch = (!isFinance && !isQC) || (isFinance && isQC);

  const [category, setCategory] = useState(defaultCategory);
  const [reason, setReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const reasons = category === "finance" ? FINANCE_REASONS : QC_REASONS;

  const isRevisit = mode === "revisit";
  const title = isRevisit
    ? `Mark for Finance Revisit${transactions.length > 1 ? ` (${transactions.length} Transactions)` : ""}`
    : `Reject ${transactions.length > 1 ? `${transactions.length} Transactions` : "Transaction"}`;
  const reasonLabel = isRevisit ? "Reason for Revisit *" : "Reason for Rejection *";
  const placeholder = isRevisit ? "Enter the reason for revisit..." : "Enter the reason for rejection...";

  const handleClose = () => { setReason(""); setCustomReason(""); setCategory(defaultCategory); onClose(); };
  const handleConfirm = () => {
    const finalReason = reason === "Others" ? `Others: ${customReason}` : reason;
    if (!finalReason) return;
    onConfirm(finalReason);
    setReason(""); setCustomReason(""); setCategory(defaultCategory);
  };

  return (
    <Modal
      open={open}
      title={title}
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>Cancel</Button>,
        <Button
          key="confirm"
          danger={!isRevisit}
          type="primary"
          style={isRevisit ? { background: "#d46b08", borderColor: "#d46b08" } : {}}
          disabled={!reason || (reason === "Others" && !customReason.trim())}
          onClick={handleConfirm}
        >
          {isRevisit ? "Mark for Revisit" : "Reject"}
        </Button>,
      ]}
      width={520}
    >
      {transactions.length > 1 && (
        <div style={{
          background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 6,
          padding: "10px 14px", fontSize: 13, color: "#8a6900", marginBottom: 16,
        }}>
          {isRevisit
            ? `Bulk revisit applies the same reason to all ${transactions.length} selected transactions.`
            : `Bulk rejection applies the same reason to all ${transactions.length} selected transactions.`}
        </div>
      )}

      {showCategorySwitch && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 8 }}>Category</div>
          <Space>
            {[{ key: "finance", label: "Finance Team" }, { key: "qc", label: "Plant QC" }].map(c => (
              <Button
                key={c.key}
                type={category === c.key ? "primary" : "default"}
                onClick={() => { setCategory(c.key); setReason(""); }}
              >
                {c.label}
              </Button>
            ))}
          </Space>
        </div>
      )}

      <div style={{ fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 8 }}>
        {reasonLabel}
      </div>
      <Radio.Group value={reason} onChange={e => setReason(e.target.value)} style={{ width: "100%" }}>
        <Space direction="vertical" style={{ width: "100%" }}>
          {reasons.map(r => (
            <Radio key={r} value={r} style={{ width: "100%", padding: "8px 12px", borderRadius: 6, border: "1px solid #f0f0f0" }}>
              {r}
            </Radio>
          ))}
        </Space>
      </Radio.Group>

      {reason === "Others" && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 8 }}>Specify Reason *</div>
          <textarea
            value={customReason}
            onChange={e => setCustomReason(e.target.value)}
            placeholder={placeholder}
            style={{
              width: "100%", minHeight: 80, padding: 10, borderRadius: 6,
              border: "1px solid #d9d9d9", fontSize: 13, resize: "vertical", fontFamily: "inherit",
            }}
          />
        </div>
      )}
    </Modal>
  );
}

// Payment Confirmation Modal
function PaymentModal({ open, tx, onConfirm, onClose }) {
  const [referenceId, setReferenceId] = useState("");
  const [loading, setLoading] = useState(false);

  const handleClose = () => { setReferenceId(""); setLoading(false); onClose(); };
  const handleConfirm = async () => {
    if (!referenceId.trim()) return;
    setLoading(true);
    try {
      await onConfirm(tx, referenceId.trim());
      setReferenceId("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      open={open}
      title="Confirm Payment Completed"
      onCancel={handleClose}
      footer={[
        <Button key="cancel" onClick={handleClose}>Cancel</Button>,
        <Button
          key="confirm"
          type="primary"
          style={{ background: "#0d7c3e", borderColor: "#0d7c3e" }}
          loading={loading}
          disabled={!referenceId.trim()}
          onClick={handleConfirm}
        >
          Confirm Payment
        </Button>,
      ]}
      width={480}
    >
      <div style={{ marginBottom: 12, fontSize: 13, color: "#555" }}>
        Invoice <strong>{tx?.invoice}</strong> — {tx?.vendorName ?? tx?.vendor}
      </div>
      <div style={{ fontSize: 13, fontWeight: 600, color: "#444", marginBottom: 8 }}>
        Payment Reference ID <span style={{ color: "#c0392b" }}>*</span>
      </div>
      <Input
        placeholder="Bank Transaction ID / Reference Number"
        value={referenceId}
        onChange={e => setReferenceId(e.target.value)}
        onPressEnter={handleConfirm}
        autoFocus
      />
      <div style={{ marginTop: 10, fontSize: 12, color: "#888" }}>
        This reference will be stored in Cortex and sent to the vendor.
      </div>
    </Modal>
  );
}

// PM-44: Modal for amending invoice values during Finance Review.
// All three fields are editable; payment_value auto-computes as total - adj.
// A mandatory amendment reason is required before saving.
function EditInvoiceModal({ open, tx, onConfirm, onClose, loading }) {
  const [total, setTotal] = useState(null);
  const [adj, setAdj] = useState(null);
  const [payment, setPayment] = useState(null);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open && tx) {
      setTotal(tx.total);
      setAdj(tx.adj);
      setPayment(tx.payment);
      setReason("");
    }
  }, [open, tx]);

  const expectedPayment = total != null && adj != null ? +(total - adj).toFixed(2) : null;
  const paymentMismatch = payment != null && expectedPayment != null && Math.abs(payment - expectedPayment) > 0.005;
  const isValid =
    total > 0 &&
    adj >= 0 &&
    payment > 0 &&
    !paymentMismatch &&
    reason.trim().length > 0;

  const handleTotalChange = (val) => {
    setTotal(val);
    if (val != null && adj != null) setPayment(+(val - adj).toFixed(2));
  };
  const handleAdjChange = (val) => {
    setAdj(val);
    if (total != null && val != null) setPayment(+(total - val).toFixed(2));
  };

  return (
    <Modal
      open={open}
      title={`Edit Invoice ${tx?.invoice ?? ""}`}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose} disabled={loading}>Cancel</Button>,
        <Button
          key="save"
          type="primary"
          disabled={!isValid}
          loading={loading}
          onClick={() => onConfirm({ total, adj, payment, reason: reason.trim() })}
        >
          Save Amendment
        </Button>,
      ]}
      width={480}
      destroyOnClose
    >
      <div style={{ background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 6, padding: "10px 14px", fontSize: 13, color: "#8a6900", marginBottom: 20 }}>
        Saving will void the existing Zoho PO and Bill, then create replacements linked to the amended values.
      </div>
      <Form layout="vertical" size="middle">
        <Form.Item label="Total Value (₹)" required>
          <InputNumber
            style={{ width: "100%" }}
            min={0.01}
            precision={2}
            value={total}
            onChange={handleTotalChange}
            prefix="₹"
          />
        </Form.Item>
        <Form.Item label="Adjustment (₹)" required>
          <InputNumber
            style={{ width: "100%" }}
            min={0}
            precision={2}
            value={adj}
            onChange={handleAdjChange}
            prefix="₹"
          />
        </Form.Item>
        <Form.Item
          label="Payment Value (₹)"
          required
          validateStatus={paymentMismatch ? "error" : ""}
          help={paymentMismatch ? `Must equal Total − Adjustment (${expectedPayment?.toFixed(2)})` : "Auto-computed as Total − Adjustment"}
        >
          <InputNumber
            style={{ width: "100%" }}
            min={0.01}
            precision={2}
            value={payment}
            onChange={setPayment}
            prefix="₹"
          />
        </Form.Item>
        <Form.Item label="Reason for Amendment" required>
          <Input.TextArea
            rows={3}
            placeholder="Describe the reason for this invoice amendment…"
            value={reason}
            onChange={e => setReason(e.target.value)}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}

// Transaction Detail View
function TransactionDetail({ tx, onBack, onAction, onResubmit, onMarkAsPaid, onComment, onEdit }) {
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showRevisitModal, setShowRevisitModal] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentLoading, setCommentLoading] = useState(false);

  const approveMenuItems = [
    {
      key: "approve",
      label: <span style={{ color: "#0d7c3e", fontWeight: 600 }}><CheckOutlined /> Final Approve</span>,
      onClick: () => onAction("approve", [tx]),
    },
    {
      key: "revisit",
      label: <span style={{ color: "#d46b08", fontWeight: 600 }}>For Finance Revisit</span>,
      onClick: () => setShowRevisitModal(true),
    },
    {
      key: "reject",
      label: <span style={{ color: "#c0392b", fontWeight: 600 }}><CloseOutlined /> Reject</span>,
      onClick: () => setShowRejectModal(true),
    },
  ];

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24 }}>
        <Space>
          <Button onClick={onBack} icon={<span>←</span>} />
          <span style={{ fontSize: 20, fontWeight: 700 }}>{tx.invoice}</span>
          <Tag color={STATUS_COLOR[tx.status]}>{tx.status}</Tag>
        </Space>

        <Space>
          {tx.status === "REJECTED" && (
            <Text type="danger" italic>Permanently Rejected</Text>
          )}
          {tx.status === "FOR FINANCE REVISIT" && (
            <>
              <Button icon={<EditOutlined />} onClick={() => onEdit(tx)}>
                Edit Invoice
              </Button>
              <Button onClick={() => onResubmit(tx)}>Re-submit for Approval</Button>
              <Button danger onClick={() => setShowRejectModal(true)}>Reject</Button>
            </>
          )}
          {tx.status === "PENDING APPROVAL" && (
            <>
              <Button icon={<EditOutlined />} onClick={() => onEdit(tx)}>
                Edit Invoice
              </Button>
              <Dropdown.Button
                type="primary"
                style={{ background: "#0d7c3e" }}
                menu={{ items: approveMenuItems }}
                onClick={() => onAction("approve", [tx])}
                icon={<DownOutlined />}
              >
                Approve
              </Dropdown.Button>
            </>
          )}
          {tx.status === "APPROVED" && (
            <Button
              type="primary"
              style={{ background: "#0d7c3e", borderColor: "#0d7c3e" }}
              icon={<CheckOutlined />}
              onClick={() => onMarkAsPaid(tx)}
            >
              Mark as Paid
            </Button>
          )}
        </Space>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24, marginBottom: 24 }}>
        <InfoCard title="Transaction Details" items={[
          ["Invoice #", tx.invoice],
          ["PO #", tx.poNumber],
          ["Date", tx.date],
          ["Auction", tx.auction],
          // ["Lot #", tx.lot],
        ]} />
        <InfoCard title="Payment Details" items={[
          ["Vendor", tx.vendorName ? `${tx.vendor} — ${tx.vendorName}` : tx.vendor],
          ["Location", tx.location],
          ["Total Value", fmt(tx.total)],
          ["Adjustment", `- ${fmt(tx.adj)}`],
          ["Payment Value", <strong key="pv">{fmt(tx.payment)}</strong>],
          ...(tx.paymentRef ? [["Payment Reference", <span key="pr" style={{ fontWeight: 600, color: "#0d7c3e" }}>{tx.paymentRef}</span>]] : []),
        ]} />
      </div>

      {tx.rejectionReason && tx.status === "FOR FINANCE REVISIT" && (
        <div style={{
          background: "#fffbe6", border: "1px solid #ffe58f", borderRadius: 8,
          padding: 16, marginBottom: 24,
        }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#d46b08", marginBottom: 6 }}>Finance Revisit Reason</div>
          <div style={{ fontSize: 13, color: "#8a6900" }}>{tx.rejectionReason}</div>
        </div>
      )}
      {tx.rejectionReason && tx.status === "REJECTED" && (
        <div style={{
          background: "#fff2f0", border: "1px solid #ffccc7", borderRadius: 8,
          padding: 16, marginBottom: 24,
        }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#cf1322", marginBottom: 6 }}>Rejection Reason</div>
          <div style={{ fontSize: 13, color: "#820014" }}>{tx.rejectionReason}</div>
        </div>
      )}

      <div style={{ background: "#fafafa", borderRadius: 10, padding: 20 }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 16 }}>
          <HistoryOutlined style={{ marginRight: 8 }} />Comments & History
        </div>
        {tx.history.map((h, i) => (
          <div key={i} style={{ display: "flex", gap: 14, position: "relative", paddingBottom: 20, paddingLeft: 26 }}>
            <div style={{
              position: "absolute", left: 6, top: 5, width: 10, height: 10, borderRadius: "50%",
              background: h.action.includes("Approved") || h.action === "Payment Completed" ? "#0d7c3e" : h.action === "For Finance Revisit" ? "#d46b08" : h.action.includes("Rejected") ? "#c0392b" : "#aaa",
              border: "2px solid #fff", boxShadow: "0 0 0 2px #ddd",
            }} />
            {i < tx.history.length - 1 && (
              <div style={{ position: "absolute", left: 10, top: 18, width: 2, height: "calc(100% - 12px)", background: "#e8e8e8" }} />
            )}
            <div>
              <div style={{ fontWeight: 600, fontSize: 13 }}>{h.action}</div>
              <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
                {h.user} • {h.time.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
              </div>
              {h.comment && <div style={{ fontSize: 13, color: "#555", marginTop: 4, fontStyle: "italic" }}>{h.comment}</div>}
            </div>
          </div>
        ))}

        {tx.status === "PENDING APPROVAL" && (
          <div style={{ marginTop: 8, display: "flex", gap: 8 }}>
            <Input.TextArea
              rows={2}
              placeholder="Add a comment…"
              value={commentText}
              onChange={e => setCommentText(e.target.value)}
              style={{ flex: 1, fontSize: 13 }}
            />
            <Button
              type="primary"
              loading={commentLoading}
              disabled={!commentText.trim()}
              onClick={async () => {
                setCommentLoading(true);
                await onComment(tx, commentText.trim());
                setCommentText("");
                setCommentLoading(false);
              }}
            >
              Add Comment
            </Button>
          </div>
        )}
      </div>

      <RejectModal
        open={showRejectModal}
        transactions={[tx]}
        onClose={() => setShowRejectModal(false)}
        onConfirm={(reason) => { onAction("reject", [tx], reason); setShowRejectModal(false); }}
      />
      <RejectModal
        mode="revisit"
        open={showRevisitModal}
        transactions={[tx]}
        onClose={() => setShowRevisitModal(false)}
        onConfirm={(reason) => { onAction("revisit", [tx], reason); setShowRevisitModal(false); }}
      />
    </div>
  );
}

function InfoCard({ title, items }) {
  return (
    <div style={{ background: "#fafafa", borderRadius: 10, padding: 18 }}>
      <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>{title}</div>
      {items.map(([k, v], i) => (
        <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: i < items.length - 1 ? "1px solid #f0f0f0" : "none" }}>
          <span style={{ fontSize: 13, color: "#888" }}>{k}</span>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

// Main Component
export default function FinalApproval({ data, loading }) {
  const [transactions, setTransactions] = useState([]);
  const locallyModified = useRef(new Set());
  const [view, setView] = useState("list");
  const [selectedTx, setSelectedTx] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectTarget, setRejectTarget] = useState(null);
  const [showRevisitModal, setShowRevisitModal] = useState(false);
  const [revisitTarget, setRevisitTarget] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentTarget, setPaymentTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const [amendLoading, setAmendLoading] = useState(false);
  const [filter, setFilter] = useState("ALL");
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [api, contextHolder] = notification.useNotification();
  const [updateInvoice] = useMutation(UPDATE_INVOICE);
  const [incrementInvoiceRejection] = useMutation(INCREMENT_INVOICE_REJECTION);
  const [insertInvoiceHistory] = useMutation(INSERT_INVOICE_HISTORY);
  const [insertVendorNotification] = useMutation(INSERT_VENDOR_NOTIFICATION);
  const [insertPurchaseOrder] = useMutation(INSERT_PURCHASE_ORDER);
  const [updatePurchaseOrderStatus] = useMutation(UPDATE_PURCHASE_ORDER_STATUS);
  const { user_id, username } = User();

  const handleApprove = (txList) => {
    const isBulk = txList.length > 1;
    Modal.confirm({
      title: isBulk ? `Approve ${txList.length} transactions?` : `Approve transaction ${txList[0].invoice}?`,
      content: "This action will final approve the selected transaction(s).",
      okText: "Approve",
      okButtonProps: { style: { backgroundColor: "#0d7c3e", borderColor: "#0d7c3e" } },
      cancelText: "Cancel",
      onOk: () => handleAction("approve", txList),
    });
  };

  useEffect(() => {
    if (!data?.invoice) return;
    setTransactions(prev => {
      const prevMap = new Map(prev.map(t => [t.id, t]));
      return data.invoice.map(inv => {
        if (locallyModified.current.has(inv.id) && prevMap.has(inv.id)) {
          return prevMap.get(inv.id);
        }
        return formatInvoice(inv);
      });
    });
  }, [data]);

  const showToast = (message, type = "success") =>
    api[type]({ message, placement: "topRight", duration: 4 });

  // Approve is gated on recording the payment in Zoho first; reject commits
  // straight away. Returns a promise so the confirm dialog shows a spinner.
  const handleAction = async (action, txList, reason) => {
    if (action !== "approve") {
      commitDecision(action, txList, reason);
      return;
    }

    const { okIds, failures } = await recordZohoPayments(txList, user_id);
    failures.forEach(f =>
      showToast(`Invoice #${f.invoice} not approved — ${f.reason}`, "error")
    );

    const approved = txList.filter(tx => okIds.has(tx.id));
    if (approved.length) commitDecision("approve", approved, reason);
    //commitDecision("approve", txList, reason);
  };

  const commitDecision = (action, txList, reason) => {
    const now = new Date();
    txList.forEach(tx => locallyModified.current.add(tx.id));

    const historyAction = action === "approve" ? "Final Approved" : action === "revisit" ? "For Finance Revisit" : "Rejected";
    const historyComment = action === "approve"
      ? "Transaction has been final approved."
      : action === "revisit"
      ? `Finance revisit reason: ${reason}`
      : `Rejection reason: ${reason}`;

    insertInvoiceHistory({
      variables: {
        objects: txList.map(tx => ({
          invoice_id: tx.id,
          action: historyAction,
          admin_user_id: parseInt(user_id),
          comment: historyComment,
        })),
      },
    });

    if (action === "reject") {
      // PM-43: increment rejection_count per-row via `_inc` — never a shared
      // Math.max value (which corrupts mixed-history rows in a bulk reject).
      incrementInvoiceRejection({
        variables: {
          ids: txList.map(tx => tx.id),
          set: {
            status: PaymentStatus.REJECTED,
            updated_by: parseInt(user_id),
            remarks: reason ?? null,
          },
        },
      });
    } else if (action === "revisit") {
      updateInvoice({
        variables: {
          ids: txList.map(tx => tx.id),
          set: {
            status: PaymentStatus.FOR_FINANCE_REVISIT,
            updated_by: parseInt(user_id),
            remarks: reason ?? null,
          },
        },
      });
    } else {
      updateInvoice({
        variables: {
          ids: txList.map(tx => tx.id),
          set: {
            status: PaymentStatus.APPROVED,
            updated_by: parseInt(user_id),
            remarks: null,
          },
        },
      });
    }

    setTransactions(prev => prev.map(t => {
      const match = txList.find(tx => tx.id === t.id);
      if (!match) return t;
      if (action === "approve") {
        return {
          ...t, status: "APPROVED", cortexStatus: "APPROVED",
          lastUpdatedBy: username, lastUpdatedAt: now,
          history: [...t.history, { action: "Final Approved", user: username, time: now, comment: "Transaction has been final approved." }],
        };
      } else if (action === "revisit") {
        return {
          ...t, status: "FOR FINANCE REVISIT", cortexStatus: "FOR FINANCE REVISIT",
          rejectionReason: reason, lastUpdatedBy: username, lastUpdatedAt: now,
          history: [...t.history, { action: "For Finance Revisit", user: username, time: now, comment: `Finance revisit reason: ${reason}` }],
        };
      } else {
        return {
          ...t, status: "REJECTED", cortexStatus: "REJECTED",
          rejectionReason: reason, rejectionCount: t.rejectionCount + 1,
          lastUpdatedBy: username, lastUpdatedAt: now,
          history: [...t.history, { action: "Rejected", user: username, time: now, comment: `Rejection reason: ${reason}` }],
        };
      }
    }));

    setSelectedRowKeys([]);

    if (action === "approve") {
      showToast(`${txList.length > 1 ? txList.length + " transactions" : "Transaction " + txList[0].invoice} approved successfully.`, "success");
      txList.forEach(tx => {
        if (tx.vendorEmail) {
          sendEmail({
            to: [{ address: tx.vendorEmail, name: tx.vendorName ?? String(tx.vendor) }],
            subject: `Payment Approved — Invoice #${tx.invoice}`,
            htmlbody: `<p>Dear ${tx.vendorName ?? tx.vendor},</p><p>We're pleased to inform you that the payment for Invoice #${tx.invoice} (${tx.poNumber}) has been approved.</p><p>The payment will now proceed as per the standard payment process.</p><p>If you have any questions, please contact the procurement team.</p><p>Kind regards,<br>Procurement Team</p>`,
          }).catch(err => console.error('sendEmail approve error', err.response?.data ?? err.message));
        } else {
          console.warn('No vendorEmail for invoice', tx.invoice);
        }
      });
    } else if (action === "revisit") {
      showToast(`${txList.length > 1 ? txList.length + " transactions" : "Transaction " + txList[0].invoice} marked for Finance Revisit.`, "warning");
      // No vendor email or push notification for Finance Revisit (PM-60 AC A.7, A.8)
    } else {
      // Reject is always final (PM-60): void Zoho bill, cancel Zoho POs, update DB PO status, notify vendor.
      showToast(`${txList.length > 1 ? txList.length + " transactions" : "Transaction " + txList[0].invoice} rejected.`, "error");
      txList.forEach(tx => {
        // 1. Void the Zoho bill — non-blocking, surface warning if it fails.
        voidZohoBill(tx.id, user_id)
          .catch(err => {
            console.error('voidZohoBill error', err.response?.data ?? err.message);
            showToast(`Invoice #${tx.invoice} rejected, but voiding its Zoho bill failed — it may need a manual void.`, "warning");
          });

        // 2. Cancel non-cancelled Zoho POs and mark them rejected in the DB.
        const activePOs = (tx.purchaseOrders ?? []).filter(p => p.zoho_po_status !== "cancelled");
        if (activePOs.length) {
          Promise.all(
            activePOs.map(p =>
              cancelZohoPO(p.id, user_id).catch(err =>
                console.warn(`cancelZohoPO(${p.id}) on reject:`, err.response?.data ?? err.message)
              )
            )
          );
          updatePurchaseOrderStatus({
            variables: { ids: activePOs.map(p => p.id), status: "rejected" },
          }).catch(err => console.error("updatePurchaseOrderStatus on reject:", err.message));
        }

        // 3. Vendor email notification.
        if (tx.vendorEmail) {
          sendEmail({
            to: [{ address: tx.vendorEmail, name: tx.vendorName ?? String(tx.vendor) }],
            subject: `Payment Rejected — Invoice #${tx.invoice}`,
            htmlbody: `<p>Dear ${tx.vendorName ?? tx.vendor},</p><p>The payment request for Invoice #${tx.invoice} (${tx.poNumber}) has been rejected.</p><p>Reason: ${reason}</p><p>For clarification, please contact the procurement team.</p><p>Kind regards,<br>Procurement Team</p>`,
          }).catch(err => console.error('sendEmail reject error', err.response?.data ?? err.message));
        }

        // 4. In-app push notification to vendor.
        insertVendorNotification({
          variables: {
            objects: [{
              user_id: tx.vendor,
              subject: `Payment Rejected — Invoice #${tx.invoice}`,
              body: `Hi ${tx.vendorName ?? tx.vendor}, your Payment Authorisation request for ${tx.poNumber} has been rejected by the Finance Team. Reason: ${reason}`,
              type: "alert",
            }],
          },
        }).catch(err => console.error("insertVendorNotification error:", err.message));
      });
    }

    if (selectedTx && txList.find(tx => tx.id === selectedTx.id)) {
      const newStatus = action === "approve" ? "APPROVED" : action === "revisit" ? "FOR FINANCE REVISIT" : "REJECTED";
      setSelectedTx(prev => ({
        ...prev,
        status: newStatus,
        cortexStatus: newStatus,
        ...(action !== "approve" ? { rejectionReason: reason } : {}),
        ...(action === "reject" ? { rejectionCount: prev.rejectionCount + 1 } : {}),
        lastUpdatedBy: username, lastUpdatedAt: now,
        history: [...prev.history, { action: historyAction, user: username, time: now, comment: historyComment }],
      }));
    }
  };

  const handleResubmit = (tx) => {
    locallyModified.current.add(tx.id);
    insertInvoiceHistory({
      variables: {
        objects: [{
          invoice_id: tx.id,
          action: "Re-submitted for Approval",
          admin_user_id: parseInt(user_id),
          comment: "Transaction re-submitted for approval.",
        }],
      },
    });
    updateInvoice({
      variables: {
        ids: [tx.id],
        set: {
          status: PaymentStatus.AUTHORISED,
          updated_by: parseInt(user_id),
          // PM-43: rejection_count is historical audit data — do NOT reset it on
          // re-submit. The one-attempt cap is enforced by the count itself.
        },
      },
    });
    const now = new Date();
    const update = t => ({
      ...t, status: "PENDING APPROVAL", cortexStatus: "PENDING",
      rejectionReason: null, lastUpdatedBy: username, lastUpdatedAt: now,
      history: [...t.history, { action: "Re-submitted for Approval", user: username, time: now, comment: "Transaction re-submitted for approval." }],
    });
    setTransactions(prev => prev.map(t => t.id === tx.id ? update(t) : t));
    if (selectedTx?.id === tx.id) setSelectedTx(prev => update(prev));
    showToast(`Transaction ${tx.invoice} re-submitted for approval.`, "info");
  };

  const handleMarkAsPaid = async (tx, referenceId) => {
    locallyModified.current.add(tx.id);
    try {
      await updateInvoice({
        variables: {
          ids: [tx.id],
          set: {
            status: PaymentStatus.PAID,
            payment_reference: referenceId,
            updated_by: parseInt(user_id),
          },
        },
      });
      await insertInvoiceHistory({
        variables: {
          objects: [{
            invoice_id: tx.id,
            action: "Payment Completed",
            admin_user_id: parseInt(user_id),
            comment: `Payment confirmed. Reference: ${referenceId}`,
          }],
        },
      });
      const now = new Date();
      const update = t => ({
        ...t,
        status: "PAID", cortexStatus: "PAID",
        paymentRef: referenceId,
        lastUpdatedBy: username, lastUpdatedAt: now,
        history: [...t.history, { action: "Payment Completed", user: username, time: now, comment: `Payment confirmed. Reference: ${referenceId}` }],
      });
      setTransactions(prev => prev.map(t => t.id === tx.id ? update(t) : t));
      if (selectedTx?.id === tx.id) setSelectedTx(prev => update(prev));
      setShowPaymentModal(false);
      setPaymentTarget(null);
      showToast(`Invoice ${tx.invoice} marked as paid.`, "success");
      if (tx.vendorEmail) {
        sendEmail({
          to: [{ address: tx.vendorEmail, name: tx.vendorName ?? String(tx.vendor) }],
          subject: `Payment Completed — Invoice #${tx.invoice}`,
          htmlbody: `<p>Dear ${tx.vendorName ?? tx.vendor},</p><p>We're pleased to confirm that the payment for Invoice #${tx.invoice} (${tx.poNumber}) has been completed.</p><p>Payment Reference: <strong>${referenceId}</strong></p><p>If you have any questions, please contact the procurement team.</p><p>Kind regards,<br>Procurement Team</p>`,
        }).catch(err => console.error("sendEmail payment completed error:", err.response?.data ?? err.message));
      }
    } catch (err) {
      locallyModified.current.delete(tx.id);
      showToast(`Failed to mark invoice ${tx.invoice} as paid — ${err.message}`, "error");
    }
  };

  const handleComment = async (tx, text) => {
    const now = new Date();
    locallyModified.current.add(tx.id);
    await insertInvoiceHistory({
      variables: {
        objects: [{
          invoice_id: tx.id,
          action: "Comment",
          admin_user_id: parseInt(user_id),
          comment: text,
        }],
      },
    });
    const newEntry = { action: "Comment", user: username, time: now, comment: text };
    setTransactions(prev => prev.map(t =>
      t.id === tx.id ? { ...t, history: [...t.history, newEntry] } : t
    ));
    if (selectedTx?.id === tx.id) setSelectedTx(prev => ({ ...prev, history: [...prev.history, newEntry] }));
  };

  // PM-44: Invoice amendment during Finance Review.
  // Sequence: void bill → cancel PO(s) → update invoice → insert new PO →
  //           sync new PO to Zoho → sync new bill → record history.
  const handleAmendInvoice = async ({ total, adj, payment, reason }) => {
    const tx = editTarget;
    if (!tx) return;
    setAmendLoading(true);

    try {
      // 1. Void existing Zoho Bill (idempotent if already void).
      await voidZohoBill(tx.id, user_id).catch(err =>
        console.warn("voidZohoBill on amend:", err.response?.data ?? err.message)
      );

      // 2. Cancel all non-cancelled Zoho POs linked to this invoice and mark
      //    them as "rejected" in Hasura so Catch Trader shows them as voided.
      const activePOs = tx.purchaseOrders.filter(p => p.zoho_po_status !== "cancelled" && p.status !== "rejected");
      await Promise.all(
        activePOs.map(p =>
          cancelZohoPO(p.id, user_id).catch(err =>
            console.warn(`cancelZohoPO(${p.id}) on amend:`, err.response?.data ?? err.message)
          )
        )
      );
      if (activePOs.length) {
        await updatePurchaseOrderStatus({
          variables: { ids: activePOs.map(p => p.id), status: "rejected" },
        });
      }

      // 3. Update invoice values and clear zoho_bill_id so the next bill sync
      //    creates a fresh Zoho Bill rather than treating the old (now void) one as current.
      await updateInvoice({
        variables: {
          ids: [tx.id],
          set: {
            total_value: total,
            adjustment: adj,
            payment_value: payment,
            zoho_bill_id: null,
            updated_by: parseInt(user_id),
          },
        },
      });

      // 4. Insert new purchase_orders row (marks the amendment for Catch Trader;
      //    the Hasura event trigger also queues a Zoho PO sync asynchronously).
      const sourcePO = tx.purchaseOrders[0];
      const newPOResult = await insertPurchaseOrder({
        variables: {
          object: {
            bid_id: sourcePO?.bid_id ?? null,
            invoice_id: tx.id,
            user_id: sourcePO?.user_id ?? tx.vendor,
            adjustment_value: adj,
            status: "pending",
          },
        },
      });
      const newPOId = newPOResult.data?.insert_purchase_orders_one?.id;

      // 5. Sync new PO to Zoho immediately (ensures zoho_po_id is set before bill sync).
      if (newPOId) {
        await syncZohoPO(newPOId, user_id).catch(err =>
          console.warn("syncZohoPO on amend:", err.response?.data ?? err.message)
        );
      }

      // 6. Sync new Zoho Bill from the updated invoice (zoho_bill_id is now null).
      await syncZohoBill(tx.id, user_id).catch(err =>
        console.warn("syncZohoBill on amend:", err.response?.data ?? err.message)
      );

      // 7. Record audit history (visible in Cortex Comments & History and in Catch Trader
      //    via the shared invoice_history table).
      const now = new Date();
      const oldPO = tx.poNumber && tx.poNumber !== "—" ? tx.poNumber : null;
      const newPO = newPOId ? `PO-${newPOId}` : null;
      const poNote = oldPO || newPO
        ? ` (${[oldPO && `Old PO: ${oldPO}`, newPO && `New PO: ${newPO}`].filter(Boolean).join(" → ")})`
        : "";
      const historyComment = `Amendment reason: ${reason}${poNote}`;
      locallyModified.current.add(tx.id);
      await insertInvoiceHistory({
        variables: {
          objects: [{
            invoice_id: tx.id,
            action: "Invoice Amended",
            admin_user_id: parseInt(user_id),
            comment: historyComment,
          }],
        },
      });

      // Optimistic local update so the UI reflects new values immediately.
      const updateTx = t => ({
        ...t,
        total: total,
        adj: adj,
        payment: payment,
        zoho_bill_id: null,
        ...(newPOId ? { poNumber: `PO-${newPOId}` } : {}),
        lastUpdatedBy: username,
        lastUpdatedAt: now,
        history: [...t.history, { action: "Invoice Amended", user: username, time: now, comment: historyComment }],
      });
      setTransactions(prev => prev.map(t => t.id === tx.id ? updateTx(t) : t));
      if (selectedTx?.id === tx.id) setSelectedTx(prev => updateTx(prev));

      showToast(`Invoice ${tx.invoice} amended successfully.`, "success");
    } catch (err) {
      console.error("handleAmendInvoice:", err);
      showToast(`Amendment failed: ${err.message}`, "error");
    } finally {
      setAmendLoading(false);
      setEditTarget(null);
    }
  };

  const filteredTx = transactions.filter(t => filter === "ALL" || t.status === filter);

  const pendingSelected = selectedRowKeys
    .map(id => transactions.find(t => t.id === id))
    .filter(t => t?.status === "PENDING APPROVAL");

  const bulkMenuItems = [
    {
      key: "approve",
      label: <span style={{ color: "#0d7c3e", fontWeight: 600 }}><CheckOutlined /> Final Approve</span>,
      onClick: () => { if (pendingSelected.length) handleApprove(pendingSelected); },
    },
    {
      key: "reject",
      label: <span style={{ color: "#c0392b", fontWeight: 600 }}><CloseOutlined /> Reject</span>,
      onClick: () => { setRejectTarget(pendingSelected); setShowRejectModal(true); },
    },
  ];

  const columns = [
    {
      title: "Invoice",
      dataIndex: "invoice",
      key: "invoice",
      render: (val, record) => (
        <a style={{ fontWeight: 600 }} onClick={() => { setSelectedTx(record); setView("detail"); }}>{val}</a>
      ),
    },
    { title: "Date", dataIndex: "date", key: "date" },
    { title: "Auction", dataIndex: "auction", key: "auction" },
    {
      title: "Vendor",
      key: "vendor",
      render: (_, r) => (
        <div>
          {r.vendorName && <div>{r.vendorName}</div>}
        </div>
      ),
    },
    { title: "Location", dataIndex: "location", key: "location" },
    { title: "Total", dataIndex: "total", key: "total", render: v => fmt(v) },
    {
      title: "Adj.",
      dataIndex: "adj",
      key: "adj",
      render: v => <span style={{ color: "#c0392b" }}>-{fmt(v)}</span>,
    },
    {
      title: "Payment",
      dataIndex: "payment",
      key: "payment",
      render: v => <strong style={{ color: "#0d7c3e" }}>{fmt(v)}</strong>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: v => <Tag color={STATUS_COLOR[v]}>{v}</Tag>,
    },
    {
      title: "Remark",
      dataIndex: "rejectionReason",
      key: "rejectionReason",
      render: v => v
        ? <span style={{ fontSize: 11, color: "#820014", background: "#fff2f0", border: "1px solid #ffccc7", borderRadius: 4, padding: "2px 8px", maxWidth: 160, display: "inline-block", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={v}>{v}</span>
        : <span style={{ color: "#bbb" }}>—</span>,
    },
    {
      title: "Last Updated By",
      key: "lastUpdated",
      render: (_, r) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 12 }}>{r.lastUpdatedBy}</div>
          <div style={{ fontSize: 11, color: "#999" }}>
            {r.lastUpdatedAt.toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size={4}>
          {(record.status === "PENDING APPROVAL" || record.status === "FOR FINANCE REVISIT") && (
            <Button size="small" icon={<EditOutlined />}
              onClick={e => { e.stopPropagation(); setEditTarget(record); }}>
              Edit
            </Button>
          )}
          {record.status === "PENDING APPROVAL" && (
            <>
              <Button size="small" type="primary" style={{ background: "#0d7c3e", borderColor: "#0d7c3e" }}
                onClick={e => { e.stopPropagation(); handleApprove([record]); }}>
                Approve
              </Button>
              <Button size="small" style={{ color: "#d46b08", borderColor: "#d46b08" }}
                onClick={e => { e.stopPropagation(); setRevisitTarget([record]); setShowRevisitModal(true); }}>
                Revisit
              </Button>
              <Button size="small" danger
                onClick={e => { e.stopPropagation(); setRejectTarget([record]); setShowRejectModal(true); }}>
                Reject
              </Button>
            </>
          )}
          {record.status === "FOR FINANCE REVISIT" && (
            <>
              <Button size="small" onClick={e => { e.stopPropagation(); handleResubmit(record); }}>Re-submit</Button>
              <Button size="small" danger
                onClick={e => { e.stopPropagation(); setRejectTarget([record]); setShowRejectModal(true); }}>
                Reject
              </Button>
            </>
          )}
          {record.status === "APPROVED" && (
            <Button
              size="small"
              type="primary"
              style={{ background: "#0d7c3e", borderColor: "#0d7c3e" }}
              onClick={e => { e.stopPropagation(); setPaymentTarget(record); setShowPaymentModal(true); }}
            >
              Mark as Paid
            </Button>
          )}
        </Space>
      ),
    },
  ];

  const filterCounts = ["ALL", "PENDING APPROVAL", "FOR FINANCE REVISIT", "APPROVED", "PAID", "REJECTED"].map(f => ({
    key: f,
    label: f === "ALL" ? "All" : f === "PENDING APPROVAL" ? "Pending" : f === "FOR FINANCE REVISIT" ? "Revisit" : f === "APPROVED" ? "Approved" : f === "PAID" ? "Paid" : "Rejected",
    count: f === "ALL" ? transactions.length : transactions.filter(t => t.status === f).length,
  }));

  return (
    <>
      {contextHolder}

      <div style={{ padding: "16px 0" }}>
        {view === "list" ? (
          <>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
              <Space size={6}>
                {filterCounts.map(f => (
                  <Button
                    key={f.key}
                    type={filter === f.key ? "primary" : "default"}
                    shape="round"
                    size="small"
                    onClick={() => setFilter(f.key)}
                  >
                    {f.label} <span style={{ opacity: 0.65 }}>({f.count})</span>
                  </Button>
                ))}
              </Space>

              {pendingSelected.length > 0 && (
                <Space>
                  <span style={{ fontSize: 13, color: "#555" }}>{pendingSelected.length} selected</span>
                  <Dropdown.Button menu={{ items: bulkMenuItems }} icon={<DownOutlined />}
                    onClick={() => handleApprove(pendingSelected)}>
                    Approve
                  </Dropdown.Button>
                </Space>
              )}
            </div>

            <Table
              rowKey="id"
              dataSource={filteredTx}
              columns={columns}
              loading={loading}
              rowSelection={{
                selectedRowKeys,
                onChange: setSelectedRowKeys,
              }}
              pagination={{ pageSize: 20, showSizeChanger: false }}
              scroll={{ x: "max-content" }}
              size="small"
              onRow={(record) => ({
                style: { cursor: "pointer" },
                onClick: () => { setSelectedTx(record); setView("detail"); },
              })}
            />
          </>
        ) : (
          <TransactionDetail
            tx={transactions.find(t => t.id === selectedTx?.id) || selectedTx}
            onBack={() => { setView("list"); setSelectedTx(null); }}
            onAction={(action, txList, reason) => action === "approve" ? handleApprove(txList) : handleAction(action, txList, reason)}
            onResubmit={handleResubmit}
            onMarkAsPaid={tx => { setPaymentTarget(tx); setShowPaymentModal(true); }}
            onComment={handleComment}
            onEdit={setEditTarget}
          />
        )}
      </div>

      <RejectModal
        open={showRejectModal}
        transactions={rejectTarget || []}
        onClose={() => { setShowRejectModal(false); setRejectTarget(null); }}
        onConfirm={(reason) => {
          handleAction("reject", rejectTarget, reason);
          setShowRejectModal(false);
          setRejectTarget(null);
        }}
      />

      <RejectModal
        mode="revisit"
        open={showRevisitModal}
        transactions={revisitTarget || []}
        onClose={() => { setShowRevisitModal(false); setRevisitTarget(null); }}
        onConfirm={(reason) => {
          handleAction("revisit", revisitTarget, reason);
          setShowRevisitModal(false);
          setRevisitTarget(null);
        }}
      />

      <PaymentModal
        open={showPaymentModal}
        tx={paymentTarget}
        onClose={() => { setShowPaymentModal(false); setPaymentTarget(null); }}
        onConfirm={handleMarkAsPaid}
      />

      <EditInvoiceModal
        open={!!editTarget}
        tx={editTarget}
        loading={amendLoading}
        onClose={() => setEditTarget(null)}
        onConfirm={handleAmendInvoice}
      />
    </>
  );
}
